import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { apiUrl } from "../apiUrl";

// Las cuentas las crea el equipo (no hay auto-registro) — ver `php artisan doctor:crear-usuario`
// en el backend. El login guarda el token de Sanctum en localStorage para que sobreviva un
// refresh de página, y lo pone en el header por defecto de axios para las próximas requests.
const TOKEN_KEY = "drclic_token";
const USER_KEY = "drclic_user";

function setAuthHeader(token) {
  if (token) axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  else delete axios.defaults.headers.common["Authorization"];
}

// Al cargar la app, si había una sesión guardada la restauramos (evita que un F5 te desloguee).
const tokenGuardado = localStorage.getItem(TOKEN_KEY);
if (tokenGuardado) setAuthHeader(tokenGuardado);

export const logoutUser = createAsyncThunk(
  "auth/logoutUser",
  async (_, { getState }) => {
    const { token } = getState().auth;
    try {
      await axios.post(
        `${apiUrl}/api/v1/logout`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (err) {
      // Si el token ya venció o el backend no responde, igual limpiamos la sesión local —
      // no tiene sentido dejar al doctor "atascado" logueado en el navegador por eso.
      console.error("Logout error:", err);
    }
  }
);

export const login = createAsyncThunk(
  "auth/login",
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`${apiUrl}/api/v1/login`, { email, password });
      return data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "No se pudo iniciar sesión."
      );
    }
  }
);

export const forgotPassword = createAsyncThunk(
  "auth/forgotPassword",
  async (email, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`${apiUrl}/api/v1/forgot-password`, { email });
      return data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "No se pudo procesar el pedido."
      );
    }
  }
);

export const resetPassword = createAsyncThunk(
  "auth/resetPassword",
  async ({ token, email, password, password_confirmation }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`${apiUrl}/api/v1/reset-password`, {
        token,
        email,
        password,
        password_confirmation,
      });
      return data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "No se pudo restablecer la contraseña."
      );
    }
  }
);

const initialState = {
  token: tokenGuardado || null,
  user: JSON.parse(localStorage.getItem(USER_KEY) || "null"),
  status: "idle",
  error: null,
};

function clearSession(state) {
  state.token = null;
  state.user = null;
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  setAuthHeader(null);
}

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout: clearSession,
  },
  extraReducers: (builder) => {
    builder
      .addCase(logoutUser.fulfilled, clearSession)
      .addCase(login.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.status = "idle";
        state.token = action.payload.token;
        state.user = action.payload.user;
        localStorage.setItem(TOKEN_KEY, action.payload.token);
        localStorage.setItem(USER_KEY, JSON.stringify(action.payload.user));
        setAuthHeader(action.payload.token);
      })
      .addCase(login.rejected, (state, action) => {
        state.status = "idle";
        state.error = action.payload || "No se pudo iniciar sesión.";
      })
      .addCase(forgotPassword.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(forgotPassword.fulfilled, (state) => {
        state.status = "idle";
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.status = "idle";
        state.error = action.payload || "No se pudo procesar el pedido.";
      })
      .addCase(resetPassword.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(resetPassword.fulfilled, (state) => {
        state.status = "idle";
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.status = "idle";
        state.error = action.payload || "No se pudo restablecer la contraseña.";
      });
  },
});

export const { logout } = authSlice.actions;
export default authSlice.reducer;

export const getAuthToken = (state) => state.auth.token;
export const getAuthUser = (state) => state.auth.user;
export const getAuthStatus = (state) => state.auth.status;
export const getAuthError = (state) => state.auth.error;
